
























































































import { defineComponent, PropType } from '@vue/composition-api';
import { ChartSet, ChartStyle, LineChartType } from '@/components/charts/model';
import { isBooleanMetric, formatMetricValue, formatMetricName } from '@/features/core/util/metric-formatters';
import { ChartOptions } from '@/components/charts/echarts/model';
import { INPUT_DATE_PICKER_MODE_META, Option } from '@/features/ui/inputs/model';
import moment, { Moment } from 'moment';
import {
  MetricDescriptorInput,
  MetricResolutionAggregator,
  MetricResolutionInput,
  MetricResolutionTimeSource,
  SpotRole,
} from '@/types/iot-portal';
import DeviceRoleMapMixin from '@/features/core/components/mixins/device-role-map';
import { CoreSpotMetricsPanelControlSpotQuery } from '@/features/core/components/spot-metrics-panel-control/__generated__/CoreSpotMetricsPanelControlSpotQuery';
import aggregationQuery from '@/features/core/components/spot-metrics-graph-panel-control/aggregation.gql';
import spotQuery from '@/features/core/components/spot-metrics-graph-panel-control/spot.gql';
import {
  CoreSpotMetricsGraphPanelControlAggregationQuery,
  CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation_first_metrics,
} from '@/features/core/components/spot-metrics-graph-panel-control/__generated__/CoreSpotMetricsGraphPanelControlAggregationQuery';
import { APOLLO_CLIENT } from '@/features/core/container/model';
import { CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics } from '@/features/core/components/spot-metrics-graph-panel-control/__generated__/CoreSpotMetricsGraphPanelControlSpotQuery';
import { ContinuousMetricPoint, DiscreteMetricPoint, normalizeMetricPoint } from '@/features/core/util/metrics';
import { DateTime } from 'luxon';
import { groupBy, partition, intersection, union } from 'lodash';
import type { ECharts } from 'echarts';
import { isDef } from '@/util/lang';
import { DeviceRole, DeviceRoleAggregationMetric } from '@/features/core/model';
import { checkMetricName } from '@/features/core/util/metric-calculations';
import { removeLabelFromName } from '@/util/heating-systems';
import { HeatingSystem, isDummyMetricValue } from '@/util/modbus';

type AggregationMetricWithLabel = CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation_first_metrics & {
  label: string;
};

type SpotMetricWithLabel = CoreSpotMetricsGraphPanelControlSpotQuery_spot_first_metrics & { label: string };

type MetricPointUnion = ContinuousMetricPoint | DiscreteMetricPoint;

const getAggregatorLabel: (aggregator: MetricResolutionAggregator) => string = (aggregator) => {
  switch (aggregator) {
    case MetricResolutionAggregator.MIN:
      return 'Minimum';
    case MetricResolutionAggregator.MAX:
      return 'Maximum';
    case MetricResolutionAggregator.MEAN:
      return 'Durchschnitt';
    case MetricResolutionAggregator.SUM:
      return 'Summe';
    default:
      return '';
  }
};

export default defineComponent({
  mixins: [DeviceRoleMapMixin],
  props: {
    name: {
      type: String,
      required: true,
      default: '',
    },
    heatingSystem: {
      type: Object as PropType<HeatingSystem>,
      required: true,
    },
    modbusMetricNames: {
      type: Array as PropType<string[]>,
      default: () => [],
      required: false,
    },
  },
  data() {
    return {
      isLoadingSpot: false,
      isLoadingMetrics: false,
      chartStyle: ChartStyle.LINE,
      chartType: LineChartType.ECHARTS,
      startDate: null as Date | null,
      stopDate: null as Date | null,
      spotId: null as string | null,
      spot: null as CoreSpotMetricsPanelControlSpotQuery['spot'] | null,
      aggregation: null as CoreSpotMetricsGraphPanelControlAggregationQuery['aggregation'] | null,
      chartOptions: {
        legend: { bottom: 60, padding: [0, 0, 0, 0] },
        grid: {
          bottom: 150,
        },
      } as ChartOptions,
      mergedMetrics: [] as AggregationMetricWithLabel[] | SpotMetricWithLabel[],
      aggregator: undefined as MetricResolutionAggregator | undefined,
      aggregationInterval: undefined as string | undefined,
      aggregationIntervalOptions: [
        { label: 'Monatlich', value: 'P1M' },
        { label: 'Wöchentlich', value: 'P1W' },
        { label: 'Täglich', value: 'P1D' },
        { label: 'Stündlich', value: 'PT1H' },
        { label: 'Halbstündlich', value: 'PT30M' },
      ] as Option[],
      selectedSpotIds: [] as string[],
      activeLegendNames: [] as string[],
      lastLegendClicked: '' as string,
      lastLegendClickTime: 0 as number,
    };
  },
  computed: {
    show(): boolean {
      return this.$store.getters.showAdvancedViewGraph;
    },
    isLoading(): boolean {
      return this.isLoadingSpot || this.isLoadingMetrics;
    },
    yAxisDimensions(): Array<string | number> {
      if (this.chartStyle === ChartStyle.STEP) {
        return ['0', '1'];
      }
      return [];
    },
    inputCellBasis(): number {
      return window.innerWidth < 1920 ? 0.5 : 0.25;
    },
    metricNames(): string[] {
      return this.spot?.first.role === SpotRole.HEATING_CONTROL
        ? this.spot?.first.metricConfigurations
            .filter((metric) => (metric.label ? this.modbusMetricNames.includes(metric.label) : false))
            .map(({ name }) => name) ?? []
        : ['temperature1', 'temperature2', 'outsideTemperature'];
    },
    datePickerStartAttrs(): JSONObject {
      const attrs: JSONObject = {};

      const mode =
        this.aggregationInterval === 'P1M' ? INPUT_DATE_PICKER_MODE_META.MONTH : INPUT_DATE_PICKER_MODE_META.DAY;

      attrs.disabledDates = {
        from:
          this.stopDate && this.aggregationInterval !== 'P1W'
            ? moment(this.stopDate).subtract(1, mode.duration).toDate()
            : this.now().toDate(),
        to: undefined,
      };

      // enable only month view
      if (this.aggregationInterval === 'P1M') {
        attrs.minimumView = mode.duration;
        attrs.maximumView = mode.duration;
      }

      return attrs;
    },
    datePickerStopAttrs(): JSONObject {
      const attrs: JSONObject = {};
      const mode =
        this.aggregationInterval === 'P1M' ? INPUT_DATE_PICKER_MODE_META.MONTH : INPUT_DATE_PICKER_MODE_META.DAY;

      const datePickerStartDate = this.startDate ? moment(this.startDate).toDate() : undefined;

      attrs.disabledDates = {
        from: this.now().add(1, mode.duration).toDate(),
        to:
          this.aggregationInterval === 'P1M'
            ? moment(datePickerStartDate).add(1, mode.duration).toDate()
            : datePickerStartDate,
      };

      // enable only month view
      if (mode !== INPUT_DATE_PICKER_MODE_META.DAY) {
        attrs.minimumView = mode.duration;
        attrs.maximumView = mode.duration;
      }

      return attrs;
    },
    metricData(): ChartSet[] {
      if (!this.mergedMetrics.length) return [];
      return this.mergedMetrics.map((metric) => ({
        label: metric.label ?? formatMetricName(metric.name),
        name: metric.name,
        color: metric.name === 'outsideTemperature' ? '#3b3939' : undefined,
        points:
          metric.__typename === 'ContinuousMetric'
            ? this.getGdprMetricPoints(metric.points)
                .map(normalizeMetricPoint)
                .map(({ time, value }) => ({ x: time, y: value }))
            : this.getGdprMetricPoints(metric.points)
                .map(normalizeMetricPoint)
                .map(({ time, value }) => ({ x: time, y: value })),
      }));
    },
    descriptorDates(): Pick<MetricDescriptorInput, 'start' | 'stop'> {
      const timezoneOffset = moment(this.startDate).utcOffset();

      const start =
        this.startDate ?? moment(this.startDate).utc().add(timezoneOffset, 'minutes').startOf('day').toDate();
      const stop = this.stopDate ?? moment(this.stopDate).add(1, 'day').startOf('day').toDate();

      return { start, stop };
    },
    possibleMetricAggregators(): MetricResolutionAggregator[] {
      return intersection<MetricResolutionAggregator>(
        union<MetricResolutionAggregator>(
          ...Array.from(this.aggregationMetricMap.values()).map(({ aggregators }) => aggregators),
        ),
        [
          MetricResolutionAggregator.MIN,
          MetricResolutionAggregator.MAX,
          MetricResolutionAggregator.MEAN,
          MetricResolutionAggregator.SUM,
        ],
      );
    },
    metricAggregatorOptions(): Option<MetricResolutionAggregator>[] {
      return this.possibleMetricAggregators.map((aggregator) => ({
        value: aggregator,
        label: getAggregatorLabel(aggregator),
      }));
    },
    aggregationMetricMap(): Map<string, DeviceRoleAggregationMetric> {
      const deviceRoleMap = this.deviceRoleMap as Record<string, DeviceRole>;

      if (this.spot?.first.role === SpotRole.HEATING_CONTROL) {
        return new Map(
          this.metricNames?.map((metric) => [
            metric,
            {
              name: metric,
              aggregators: [MetricResolutionAggregator.MEAN],
              timeSource: MetricResolutionTimeSource.START,
            },
          ]),
        );
      }
      return new Map(
        deviceRoleMap[this.spot?.first.role ?? '']?.aggregationMetricNames?.map((metric) => [metric.name, metric]),
      );
    },
    aggregationResolutionMap(): Map<string, MetricResolutionInput | undefined> {
      return new Map(
        Array.from(this.aggregationMetricMap.entries())
          .filter((entry) => (this.aggregator ? entry[1].aggregators.includes(this.aggregator) : true))
          .map(([aggregationMetricName, metric]: [string, DeviceRoleAggregationMetric]) => {
            if (metric === undefined) {
              return [aggregationMetricName, undefined];
            }

            return [
              aggregationMetricName,
              {
                intervalLength: this.aggregationInterval ?? 'PT1H',
                timeSource: metric.timeSource,
                aggregator: this.aggregator,
              },
            ];
          }),
      );
    },
    getHeatingSystemComponents(): [string, string][] {
      const heatingSystemComponents = new Map<string, string>(
        this.heatingSystem.heatingSystemMeasurementGroups.flatMap(({ heatingSystemMeasurements }) => {
          return heatingSystemMeasurements.map((measurement) => {
            return [measurement.spotId, removeLabelFromName(measurement.measurementName, measurement.metricLabel)];
          });
        }),
      );

      return Array.from(heatingSystemComponents);
    },
    allLegendsSelected(): boolean {
      return this.mergedMetrics.length > 0 && this.mergedMetrics.every((m) => this.activeLegendNames.includes(m.label));
    },
  },
  watch: {
    spotId(val) {
      this.fetchData(val);
    },
  },
  beforeMount() {
    this.startDate = moment().subtract(30, 'days').startOf('day').toDate();
    this.stopDate = moment().toDate();
    this.selectedSpotIds = this.getHeatingSystemComponents.map(([id]: [string, string]) => id);
  },
  async mounted() {
    await this.fetchSelectedComponentsData();
  },
  methods: {
    async fetchData(spotId: string) {
      await this.fetchSpot(spotId);
      await this.fetchMetrics(spotId);
    },
    yAxisLabelFormatter(value: string | number): string {
      if (this.chartStyle === ChartStyle.STEP && this.metricNames.some(isBooleanMetric)) {
        return value === '0' ? 'no/off' : 'yes/on';
      }
      return this.metricNames.length === 0 ? `${value}` : `${this.formatValue(value, this.metricNames[0]) ?? value}`;
    },
    formatValue(value: string | number, name: string): string {
      return formatMetricValue(this.metricNames.find((v) => v === name) ?? '', value);
    },
    onUpdateStartDate(date: Date): void {
      if (this.aggregationInterval === 'P1W') {
        date = moment(date).startOf('isoWeek').toDate();
        this.onUpdateStopDate(moment(date).endOf('isoWeek').toDate());
      }
      this.startDate = date;
      this.fetchSelectedComponentsData();
    },
    onUpdateStopDate(date: Date | undefined): void {
      let updatedDate = moment().toDate();
      if (date) {
        updatedDate = moment(date)
          .endOf(this.aggregationInterval === 'P1M' ? 'month' : 'day')
          .toDate();
      }

      this.stopDate = updatedDate;
      this.fetchSelectedComponentsData();
    },
    now(): Moment {
      return moment();
    },
    updateAggregator(aggregator: MetricResolutionAggregator): void {
      this.aggregator = aggregator;
      this.fetchSelectedComponentsData();
    },
    updateAggregationInterval(interval: Duration): void {
      this.onUpdateStopDate(undefined);
      this.aggregationInterval = interval;
      this.fetchSelectedComponentsData();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getGdprMetricPoints(metricPoints: any[]): any[] {
      if (
        ![SpotRole.HEAT_COST_ALLOCATOR, SpotRole.HEAT_METER_COUNTER, SpotRole.WATER_METER_HOT].includes(
          this.spot?.first.role ?? SpotRole.UNKNOWN,
        )
      ) {
        return metricPoints;
      }
      const now = DateTime.utc();

      const [newerThan2Weeks, olderThan2Weeks] = partition(
        metricPoints,
        ({ time }) => DateTime.fromISO(time).plus({ weeks: 2 }) >= now,
      );

      const pointsGroupedByYearMonth = groupBy(
        olderThan2Weeks,
        ({ time }) => `${DateTime.fromISO(time).year}-${DateTime.fromISO(time).month}`,
      );

      const filteredPointsOlderThan2Weeks = Object.values(pointsGroupedByYearMonth).flatMap((points) =>
        this.filter1thAnd16th(points),
      );

      const gdprPoints = [...newerThan2Weeks, ...filteredPointsOlderThan2Weeks].sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
      );

      return gdprPoints;
    },
    filter1thAnd16th(metricPoints: MetricPointUnion[]): MetricPointUnion[] {
      const firstOfMonth = metricPoints.find(({ time }) => {
        return DateTime.fromISO(time).hasSame(DateTime.fromISO(time).startOf('month'), 'day');
      });

      const sixteenthOfMonth = metricPoints.find(({ time }) => {
        const date = DateTime.fromISO(time);

        return DateTime.fromISO(time).hasSame(DateTime.utc(date.year, date.month, 16), 'day');
      });

      if (firstOfMonth === undefined && sixteenthOfMonth === undefined) {
        return [];
      }

      return [firstOfMonth, sixteenthOfMonth].filter(isDef);
    },
    async fetchSpot(spotId: string) {
      try {
        this.isLoadingSpot = true;
        const { data } = await this.$apollo.query<CoreSpotMetricsPanelControlSpotQuery>({
          query: spotQuery,
          fetchPolicy: 'no-cache',
          variables: {
            spotId,
          },
        });
        this.spot = data.spot;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.isLoadingSpot = false;
      }
    },
    async fetchMetrics(spotId: string) {
      try {
        const { data } = await this.$apollo.query<CoreSpotMetricsGraphPanelControlAggregationQuery>({
          query: aggregationQuery,
          client: APOLLO_CLIENT.PORTAL_CLIENT,
          variables: {
            descriptors: Array.from(this.aggregationResolutionMap.entries())
              .filter(([name]) => this.metricNames.map((metricName) => checkMetricName(metricName)).includes(name))
              .map(([name, resolution]) => ({
                name: checkMetricName(name),
                resolution,
                take: 100_000,
                ...this.descriptorDates,
              })),
            spotId,
            includeOutsideTemperature: true,
          },
          fetchPolicy: 'no-cache',
        });
        this.aggregation = {
          ...data.aggregation,
          first: {
            ...data.aggregation.first,
            metrics: data.aggregation.first.metrics
              .map((metric: CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation_first_metrics) => {
                if (metric.__typename === 'ContinuousMetric') {
                  return {
                    ...metric,
                    points: metric.points.filter((p) => !isDummyMetricValue(p.continuous)),
                  };
                }
                return metric; // Keep other types like DiscreteMetric unchanged
              })
              .filter((metric: CoreSpotMetricsGraphPanelControlAggregationQuery_aggregation_first_metrics) => {
                // Keep other types like DiscreteMetric unchanged otherwise check for metric points
                return metric.__typename !== 'ContinuousMetric' || metric.points.length > 0;
              }),
          },
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    async fetchSelectedComponentsData() {
      this.isLoadingSpot = true;
      this.isLoadingMetrics = true;
      this.mergedMetrics = [];

      const allMetrics: AggregationMetricWithLabel[] = [];

      for (const spotId of this.selectedSpotIds) {
        await this.fetchSpot(spotId); // sets this.spot
        this.aggregation = null;
        await this.fetchMetrics(spotId); // sets this.aggregation
        const aggregation = this.aggregation as CoreSpotMetricsGraphPanelControlAggregationQuery['aggregation'] | null;
        const metrics = aggregation?.first.metrics ?? [];

        metrics.forEach((metric) => {
          allMetrics.push({
            ...metric,
            name: metric.name,
            label:
              this.spot?.first.metricConfigurations.find(({ name }) => name === metric.name)?.label ??
              formatMetricName(metric.name),
          });
        });
      }

      this.mergedMetrics = allMetrics;

      // Ensure all legends are selected on first load if none are selected
      if (!this.activeLegendNames.length) {
        this.activeLegendNames = this.mergedMetrics.map((m) => m.label);
      }
      this.activeLegendNames = this.activeLegendNames.filter((label) =>
        this.mergedMetrics.some((m) => m.label === label),
      );

      this.isLoadingMetrics = false;
    },
    onChartLegendChange(event: { name: string; selected: Record<string, boolean> }) {
      const clickedLegend = event.name;
      const isActive = this.activeLegendNames.includes(clickedLegend);
      const now = Date.now();

      if (isActive) {
        if (this.activeLegendNames.length === 1) {
          return;
        }
        this.activeLegendNames = [clickedLegend];
        this.$nextTick(() => {
          const chartComponent = this.$refs.chartRef as Vue & { getEchartsInstance?: () => ECharts | undefined };
          const chart = chartComponent?.getEchartsInstance?.();
          if (chart) {
            this.mergedMetrics.forEach((m) => {
              chart.dispatchAction({
                type: m.label === clickedLegend ? 'legendSelect' : 'legendUnSelect',
                name: m.label,
              });
            });
          }
        });
      } else {
        // Default: toggle logic
        this.activeLegendNames = Object.keys(event.selected).filter((label) => event.selected[label]);
      }

      this.lastLegendClicked = clickedLegend;
      this.lastLegendClickTime = now;
    },
    handleSelectAllLegends() {
      // select all legends if not already selected, on this button click
      if (this.allLegendsSelected) return;
      this.activeLegendNames = this.mergedMetrics.map((m) => m.label);

      this.$nextTick(() => {
        const chartComponent = this.$refs.chartRef as Vue & { getEchartsInstance?: () => ECharts | undefined };
        const chart = chartComponent?.getEchartsInstance?.();
        if (chart) {
          this.mergedMetrics.forEach((m) => {
            chart.dispatchAction({
              type: 'legendSelect',
              name: m.label,
            });
          });
        }
      });
    },
  },
});
